import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Cpu, ShieldCheck } from 'lucide-react';
import { useConsentTexts, useRecordConsent } from '@/api/privacy';
import type { ConsentKind, DeviceDetails, ScreenVideoInfo } from '@/api/types';
import { useMe } from '@/api/auth';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { LoadingBlock } from '@/components/ui/States';
import { formatDateTime } from '@/lib/format';
import { toastError } from '@/lib/errors';
import { cn } from '@/lib/cn';

/** A notice or consent text, paragraph by paragraph (bullets kept). */
export function ConsentText({ kind, className }: { kind: ConsentKind; className?: string }) {
  const { i18n } = useTranslation();
  const texts = useConsentTexts(i18n.language);
  const text = texts.data?.find((x) => x.kind === kind);
  if (!text) return <LoadingBlock rows={6} />;
  return (
    <div className={cn('flex flex-col gap-2 text-sm leading-relaxed', className)} data-testid={`consent-text-${kind}`}>
      {text.body.split('\n').filter((l) => l.trim()).map((line, i) => (
        <p key={i} className={cn(/^\d+\./.test(line) && 'mt-1 font-semibold', line.startsWith('•') && 'pl-3')}>{line}</p>
      ))}
      <p className="text-xs text-ink-3">{text.version}</p>
    </div>
  );
}

export function ConsentTextDialog({ kind, open, onOpenChange }: { kind: ConsentKind; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const texts = useConsentTexts(i18n.language);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" title={texts.data?.find((x) => x.kind === kind)?.title ?? t('privacy.loading')}
      footer={<Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>}>
      <ConsentText kind={kind} />
    </Dialog>
  );
}

/**
 * Shown before anything else when the signed-in person has not acknowledged the current privacy notice (a new version, or an
 * account from before notices were recorded). It cannot be dismissed, only accepted or left by signing out.
 */
export function ConsentGate() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { logout } = useAuth();
  const record = useRecordConsent();
  const texts = useConsentTexts(i18n.language);
  const pending = me?.pendingConsents ?? [];
  if (!pending.includes('PrivacyNotice')) return null;
  const text = texts.data?.find((x) => x.kind === 'PrivacyNotice');
  return (
    <Dialog open onOpenChange={() => undefined} dismissible={false} size="lg" title={text?.title ?? t('privacy.loading')} description={t('privacy.gateIntro')}
      footer={(
        <>
          <Button variant="ghost" onClick={() => void logout()}>{t('privacy.signOut')}</Button>
          <Button variant="primary" icon={<ShieldCheck className="size-4" />} loading={record.isPending} disabled={!text}
            onClick={() => text && record.mutate({ kind: 'PrivacyNotice', version: text.version, granted: true }, { onError: (e) => toastError(e) })}>
            {t('privacy.acknowledge')}
          </Button>
        </>
      )}>
      <ConsentText kind="PrivacyNotice" />
    </Dialog>
  );
}

/** Testers who have not answered the device-details consent are asked once, when they start a test; either answer is fine. */
export function DeviceConsentPrompt({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const record = useRecordConsent();
  const texts = useConsentTexts(i18n.language);
  const [reading, setReading] = useState(false);
  if (!me || me.deviceDetailsConsent !== null) return null;
  const version = texts.data?.find((x) => x.kind === 'DeviceDetails')?.version;
  const answer = (granted: boolean) => version && record.mutate({ kind: 'DeviceDetails', version, granted }, {
    onSuccess: () => toast.success(granted ? t('privacy.deviceGranted') : t('privacy.deviceDeclined')),
    onError: (e) => toastError(e),
  });
  return (
    <section className={cn('panel flex flex-col gap-3 p-4', className)} data-testid="device-consent">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Cpu className="size-4 text-accent" />{t('privacy.deviceAskTitle')}</h2>
      <p className="text-sm text-ink-2">{t('privacy.deviceAskBody')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="primary" disabled={!version} loading={record.isPending && record.variables?.granted === true} onClick={() => answer(true)}>
          {t('privacy.deviceAllow')}
        </Button>
        <Button size="sm" variant="secondary" disabled={!version} loading={record.isPending && record.variables?.granted === false} onClick={() => answer(false)}>
          {t('privacy.deviceDeny')}
        </Button>
        <button type="button" className="text-sm text-accent-text hover:underline" onClick={() => setReading(true)}>{t('privacy.readFull')}</button>
      </div>
      <ConsentTextDialog kind="DeviceDetails" open={reading} onOpenChange={setReading} />
    </section>
  );
}

// ---------------------------------------------------------------------- showing device facts
type Row = [label: string, value: ReactNode];

function splitTopLevel(s: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '(') depth++;
    else if (s[i] === ')') depth--;
    else if (s[i] === ',' && depth === 0) {
      parts.push(s.slice(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(s.slice(start).trim());
  return parts;
}

/** The graphics card's name out of the WebGL string ("ANGLE (NVIDIA, NVIDIA GeForce RTX 3060 Direct3D11 …)" → "NVIDIA GeForce RTX 3060"). */
export function gpuName(renderer: string | null | undefined): string | null {
  if (!renderer?.trim()) return null;
  let s = renderer.trim();
  if (s.startsWith('ANGLE (') && s.endsWith(')')) {
    const parts = splitTopLevel(s.slice(7, -1));
    s = parts[1] ?? parts[0];
  }
  s = s.replace(/ \(0x[0-9a-f]+\)/gi, '').replace(/ (Direct3D\S*|vs_\d|OpenGL|D3D1\d).*$/, '').replace('ANGLE Metal Renderer: ', '').trim();
  return s || renderer.trim();
}

export function deviceRows(d: DeviceDetails, t: (k: string, o?: Record<string, unknown>) => string): Row[] {
  const rows: Row[] = [];
  const add = (label: string, value: ReactNode | null | undefined | false) => {
    if (value !== null && value !== undefined && value !== false && value !== '') rows.push([t(`device.${label}`), value]);
  };
  add('os', [d.osName, d.osVersion].filter(Boolean).join(' ') + (d.bitness ? ` (${d.bitness} bit${d.architecture ? `, ${d.architecture}` : ''})` : d.architecture ? ` (${d.architecture})` : ''));
  add('model', d.model);
  add('formFactor', d.formFactor ? t(`device.formFactors.${d.formFactor}`, { defaultValue: d.formFactor }) : null);
  add('browser', [d.browser, d.browserVersion].filter(Boolean).join(' '));
  if (d.screenWidth && d.screenHeight)
    add('screen', `${d.screenWidth}×${d.screenHeight}` + (d.pixelRatio ? ` @${d.pixelRatio}x` : '') + (d.colorDepth ? ` · ${d.colorDepth} bit` : '') +
      (d.orientation ? ` · ${d.orientation}` : ''));
  if (d.availableWidth && d.availableHeight) add('available', `${d.availableWidth}×${d.availableHeight}`);
  add('cpu', d.cpuCores ? t('device.cores', { count: d.cpuCores }) : null);
  add('memory', d.memoryGb ? (d.memoryGb >= 8 ? t('device.memoryAtLeast', { gb: 8 }) : `${d.memoryGb} GB`) : null);
  const gpu = gpuName(d.gpuRenderer ?? d.gpuVendor);
  const raw = d.gpuRenderer ?? d.gpuVendor;
  add('gpu', gpu && (gpu === raw ? gpu : (
    <>
      {gpu}
      <span className="mono block text-xs text-ink-3">{raw}</span>
    </>
  )));
  add('network', [d.connectionType, d.downlinkMbps != null ? `${d.downlinkMbps} Mbps` : null, d.rttMs != null ? `${d.rttMs} ms` : null,
    d.saveData ? t('device.saveData') : null].filter(Boolean).join(' · '));
  add('storage', d.storageQuotaMb ? `${(d.storageQuotaMb / 1024).toFixed(d.storageQuotaMb >= 10240 ? 0 : 1)} GB` : null);
  add('touch', d.touchPoints != null ? (d.touchPoints > 0 ? t('device.touchPoints', { count: d.touchPoints }) : t('device.noTouch')) : null);
  add('theme', [d.colorScheme ? t(`device.scheme.${d.colorScheme}`, { defaultValue: d.colorScheme }) : null, d.reducedMotion ? t('device.reducedMotion') : null]
    .filter(Boolean).join(' · '));
  add('languages', d.languages?.length ? d.languages.join(', ') : null);
  return rows;
}

export function videoRows(v: ScreenVideoInfo, t: (k: string, o?: Record<string, unknown>) => string, lang: string): Row[] {
  const rows: Row[] = [];
  const device = [v.make, v.model].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' ');
  if (device) rows.push([t('device.videoDevice'), device]);
  const os = [v.osName, v.osVersion].filter(Boolean).join(' ');
  if (os) rows.push([t('device.os'), os]);
  if (v.width && v.height) rows.push([t('device.videoSize'), `${v.width}×${v.height}`]);
  if (v.recordedAt) rows.push([t('device.recordedAt'), formatDateTime(v.recordedAt, lang)]);
  return rows;
}

/** "Windows 11 · 16 çekirdek · RTX 3060" (short, for headers). */
export function deviceShort(d: DeviceDetails | null | undefined, v: ScreenVideoInfo | null | undefined, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (d) {
    return [[d.osName, d.osVersion].filter(Boolean).join(' '), d.model, d.cpuCores ? t('device.cores', { count: d.cpuCores }) : null, gpuName(d.gpuRenderer ?? d.gpuVendor)]
      .filter(Boolean).join(' · ');
  }
  if (v) return [[v.make, v.model].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' '), [v.osName, v.osVersion].filter(Boolean).join(' '),
    v.width && v.height ? `${v.width}×${v.height}` : null].filter(Boolean).join(' · ');
  return '';
}

export function FactList({ rows, className }: { rows: Row[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm', className)}>
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-ink-3">{label}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Everything known about the device of a run: the SDK's details (with consent) and what the screen recording says. */
export function DeviceDetailsView({ details, video }: { details: DeviceDetails | null | undefined; video: ScreenVideoInfo | null | undefined }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col gap-4" data-testid="device-details">
      {details && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-3">{t('device.fromBrowser')}</h3>
          <FactList rows={deviceRows(details, t)} />
          <p className="mt-2 text-xs text-ink-3">{t('device.collectedAt', { at: formatDateTime(details.collectedAt, i18n.language) })}</p>
        </section>
      )}
      {video && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-3">{t('device.fromVideo')}</h3>
          <FactList rows={videoRows(video, t, i18n.language)} />
        </section>
      )}
      {!details && !video && <p className="text-sm text-ink-2">{t('device.none')}</p>}
    </div>
  );
}
